import axios from 'axios';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import type { Env } from 'types';
import { JotaiProvider, jotaiStore, logHistoryAtom } from '../../stores/jotai.store';
import { environmentService } from '../../stores/store';
import { validateEnv } from 'utils/env/validate-env';
import DOMPurify from 'dompurify';
import { logger } from '@sanlam-glacier/app-common/modules/logger';
import { LogMessageWithMeta } from 'types/logging/meta-log.type';

const EnvContext = createContext<(Env & { setConfig: (env: Env) => void }) | undefined>(undefined);
EnvContext.displayName = 'Environment Variables Configuration';

const setTitle = (title: string) => {
  document.title = title;
};

const getEnvUrl = (baseUrl: string, routerBasePath?: string) => {
  const resolvedRouterPath = routerBasePath ?? '';
  const newUrl = baseUrl + resolvedRouterPath + 'config/__ENV.json';
  const formattedUrl = newUrl.replaceAll('//', '/');
  return formattedUrl;
};

export const ConfigProvider = ({ children }: PropsWithChildren) => {
  const [config, setConfig] = useState({
    apiUrl: import.meta.env.VITE_API_URL,
    tenant: import.meta.env.VITE_TENANT,
    test: import.meta.env.VITE_TEST,
    routerBasePath: import.meta.env.VITE_ROUTER_BASE_PATH,
    applicationName: import.meta.env.VITE_APPLICATION_NAME,
    versionId:
      import.meta.env.MODE === 'development' ? 'LOCAL_BUILD' : import.meta.env.VITE_VERSION_ID,
    idleTimeoutSeconds: 3600,
    currencyCountryCode: 'ZAR',
    currencyPrefix: 'R',
    supportEmail: '[placeholder]',
    theme: 'glacier',
    featureFlags: {
      requestLogging: import.meta.env.VITE_FEATURE_REQUEST_LOGGING,
      formDevTools: false,
      enableSigningDirectAccess: false,
      isTestingBankVerification: false,
      showOtherApps: false,
      showDashboard: false,
      showClient: false,
      showReports: false,
      shouldDisplayMaintenanceBanner: true,
      passwordProtected: false,
      enableCloning: false,
      isComparisonsDisabled: true,
      devtools: true,
      stepperFeatureToggle: true,
      passGrantDenyPopupParam: false,
      exposureFeatureToggle: true,
      eacFeatureToggle: true,
      enableUnitTransferOnEndAndSnkFeatureToggle: true
    }
  } as Env);
  const [doneLoadingConfig, setDoneLoadingConfig] = useState(false);

  useEffect(() => {
    axios
      .get(getEnvUrl(import.meta.env.BASE_URL))
      .then((res) => {
        const validatedConfig = validateEnv(res.data, config);
        setConfig(validatedConfig);
        environmentService.setStore(validatedConfig);
        setDoneLoadingConfig(true);
        const sanitizedApplicationName = DOMPurify.sanitize(validatedConfig.applicationName);
        setTitle(sanitizedApplicationName);
        logger.setConfig({
          logLevel: validatedConfig.logLevel,
          shouldTrace: import.meta.env.MODE === 'development',
          onLog: (log, level) => {
            const logToStore: LogMessageWithMeta = {
              ...log,
              level,
              timestamp: new Date()
            };
            const logHistory = jotaiStore.get(logHistoryAtom);
            const updatedLogHistory = [...logHistory, logToStore];
            jotaiStore.set(logHistoryAtom, updatedLogHistory);
          }
        });
      })
      // biome-ignore lint/suspicious/noConsole: JW just log errors to console for now ...
      .catch((err) => console.error(err));
  }, []);

  return (
    <EnvContext.Provider value={{ ...config, setConfig }}>
      <JotaiProvider store={jotaiStore}>{doneLoadingConfig && children}</JotaiProvider>
    </EnvContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(EnvContext);

  if (!context) {
    throw new Error('Cannot use config outside of a Config Context Provider');
  }

  return context;
};
